import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const Modal = ({ onClose, title, children }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Add slight delay to ensure smooth animation
        const timer = setTimeout(() => {
            setIsVisible(true);
            // Prevent body scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }, 10);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = 'auto';
        };
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const handleOutsideClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={handleOutsideClick}
            >
                <motion.div
                    className="absolute inset-0 bg-black/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                />

                <motion.div
                    className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl"
                    initial={{ scale: 0.95, y: 20, opacity: 0 }}
                    animate={{
                        scale: isVisible ? 1 : 0.95,
                        y: isVisible ? 0 : 20,
                        opacity: isVisible ? 1 : 0
                    }}
                    exit={{ scale: 0.95, y: 20, opacity: 0 }}
                    transition={{
                        type: "spring",
                        damping: 25,
                        stiffness: 300
                    }}
                >
                    <div className="relative px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm bg-white/90 dark:bg-gray-900/90">
                        <div className="flex items-center justify-between">
                            <h3 className="font-medium text-lg text-gray-900 dark:text-white">{title}</h3>
                            <motion.button
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleClose}
                            >
                                <X size={16} />
                            </motion.button>
                        </div>
                    </div>

                    {/* Content area with inset shadow */}
                    <div
                        className="p-6 overflow-y-auto thin-scrollbar"
                    >
                        {children}
                    </div>
                </motion.div>
            </motion.div>

            <style jsx global>{`
                .thin-scrollbar::-webkit-scrollbar {
                    width: 5px;
                }
                
                .thin-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                
                .thin-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(155, 155, 155, 0.5);
                    border-radius: 20px;
                }
                
                .thin-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
                }
                
                @media (prefers-color-scheme: dark) {
                    .thin-scrollbar::-webkit-scrollbar-thumb {
                        background-color: rgba(100, 100, 100, 0.5);
                    }
                    
                    .thin-scrollbar {
                        scrollbar-color: rgba(100, 100, 100, 0.5) transparent;
                    }
                }
            `}</style>
        </AnimatePresence>
    );
};

export default Modal;